/**
 * Pricing Chart Component
 * Lightweight Charts integration for displaying pricing trends
 */

import { LitElement, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ref, createRef, Ref } from 'lit/directives/ref.js';
import {
  createChart,
  IChartApi,
  ISeriesApi,
  LineData,
  ColorType,
  SeriesType,
  LineSeries,
} from 'lightweight-charts';
import type { ChartSeries } from '../types/pricing.js';
import { getThemeColor, getSeriesColor } from '../utils/series-colors.js';

@customElement('pricing-chart')
export class PricingChart extends LitElement {
  // CRITICAL: Use Light DOM for canvas rendering
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Array })
  chartSeries: ChartSeries[] = [];

  private chart?: IChartApi;

  private seriesMap: Map<string, ISeriesApi<SeriesType>> = new Map();

  private containerRef: Ref<HTMLDivElement> = createRef();

  /**
   * Initialize the chart
   */
  protected firstUpdated() {
    if (!this.containerRef.value) return;

    // Get theme colors
    const backgroundColor = getThemeColor(
      '--color-surface-base',
      '#0D0D0D'
    );
    const textColor = getThemeColor('--color-text-primary', '#ffffff');
    const gridColor = getThemeColor('--color-surface-border', '#333333');

    // Create chart with theme-aware configuration
    this.chart = createChart(this.containerRef.value, {
      autoSize: true,
      layout: {
        background: {
          type: ColorType.Solid,
          color: backgroundColor,
        },
        textColor: textColor,
        // Hide TradingView attribution logo (v5)
        attributionLogo: false,
      },
      grid: {
        vertLines: {
          color: gridColor,
        },
        horzLines: {
          color: gridColor,
        },
      },
      timeScale: {
        borderColor: gridColor,
        timeVisible: true,
        secondsVisible: false,
        // Prevent showing empty zones - fit to actual data
        rightOffset: 0,
        barSpacing: 6,
        fixLeftEdge: true,
        fixRightEdge: true,
        // Style axis labels with grey tones
        tickMarkFormatter: undefined,
      },
      rightPriceScale: {
        borderColor: gridColor,
        // Disable y-axis scaling interaction
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      // Disable y-axis interaction handlers
      handleScale: {
        axisPressedMouseMove: {
          time: true,
          price: false, // Disable y-axis scaling
        },
        mouseWheel: false,
        pinch: false,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false, // Disable vertical touch drag
      },
      crosshair: {
        horzLine: {
          color: getThemeColor('--color-text-tertiary', '#9ca3af'),
          width: 1,
          style: 0, // Solid line
          labelBackgroundColor: getThemeColor('--color-text-secondary', '#6b7280'),
        },
        vertLine: {
          color: getThemeColor('--color-text-tertiary', '#9ca3af'),
          width: 1,
          style: 0, // Solid line
          labelBackgroundColor: getThemeColor('--color-text-secondary', '#6b7280'),
        },
      },
    });

    // Initialize series with data
    this.updateChartSeries();
  }

  /**
   * Update chart when properties change
   */
  protected willUpdate(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('chartSeries') && this.chart) {
      this.updateChartSeries();
    }
  }

  /**
   * Update or recreate chart series
   */
  private updateChartSeries() {
    if (!this.chart) return;

    // Remove existing series
    this.seriesMap.forEach((series) => {
      this.chart!.removeSeries(series);
    });
    this.seriesMap.clear();

    let minTime: number | undefined;
    let maxTime: number | undefined;

    // Add new series
    this.chartSeries.forEach((series) => {
      if (series.data.length === 0) return;

      const lineSeries = this.chart!.addSeries(LineSeries);

      // Apply series options with color from theme
      lineSeries.applyOptions({
        color: getSeriesColor(series.name),
        lineWidth: 2,
        title: series.name,
        priceFormat: {
          type: 'price',
          precision: 2,
          minMove: 0.01,
        },
        // Hide the price line (dotted horizontal line at last value)
        priceLineVisible: false,
        lastValueVisible: false,
      });

      // Convert data to LineData format
      const lineData: LineData[] = series.data.map((point) => ({
        time: point.time as LineData['time'],
        value: point.value,
      }));

      // Track min/max time across all series
      if (lineData.length > 0) {
        const firstTimeValue = lineData[0].time;
        const lastTimeValue = lineData[lineData.length - 1].time;

        const firstTime = typeof firstTimeValue === 'string'
          ? new Date(firstTimeValue).getTime() / 1000
          : (firstTimeValue as number);
        const lastTime = typeof lastTimeValue === 'string'
          ? new Date(lastTimeValue).getTime() / 1000
          : (lastTimeValue as number);

        minTime = minTime === undefined ? firstTime : Math.min(minTime, firstTime);
        maxTime = maxTime === undefined ? lastTime : Math.max(maxTime, lastTime);
      }

      lineSeries.setData(lineData);
      this.seriesMap.set(series.name, lineSeries);
    });

    // Set visible range to actual data range to prevent empty zones
    if (minTime !== undefined && maxTime !== undefined) {
      this.chart.timeScale().setVisibleRange({
        from: minTime as LineData['time'],
        to: maxTime as LineData['time'],
      });
    } else {
      // Fallback to fitContent if no data
      this.chart.timeScale().fitContent();
    }
  }

  /**
   * Cleanup chart on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    // CRITICAL: Remove chart to prevent memory leaks
    if (this.chart) {
      this.chart.remove();
      this.chart = undefined;
    }

    this.seriesMap.clear();
  }

  render() {
    return html`
      <div
        ${ref(this.containerRef)}
        class="w-full min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
      ></div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pricing-chart': PricingChart;
  }
}
